import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Loader2, TrendingUp, Target, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { getAIRecommendation } from '@/lib/api';
import { toast } from 'sonner';

const PRIORITIES = [
  { id: 'cashback', label: 'Maximum Cashback' },
  { id: 'travel_rewards', label: 'Travel Rewards & Miles' },
  { id: 'low_fee', label: 'Low/No Annual Fee' },
  { id: 'lounge_access', label: 'Airport Lounge Access' },
  { id: 'islamic', label: 'Sharia-Compliant (Islamic)' },
  { id: 'premium_perks', label: 'Premium Lifestyle Perks' },
];

export default function AIRecommendPage() {
  const [form, setForm] = useState({
    salary: '', monthly_spend: '',
    dining_percent: '20', travel_percent: '15', shopping_percent: '25',
    groceries_percent: '20', fuel_percent: '10', entertainment_percent: '10',
  });
  const [priorities, setPriorities] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const togglePriority = (id) => {
    setPriorities(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.salary || !form.monthly_spend) { toast.error('Please fill in salary and monthly spend'); return; }
    setLoading(true);
    setRecommendations(null);
    try {
      const payload = {
        salary: parseFloat(form.salary),
        monthly_spend: parseFloat(form.monthly_spend),
        dining_percent: parseFloat(form.dining_percent) || 0,
        travel_percent: parseFloat(form.travel_percent) || 0,
        shopping_percent: parseFloat(form.shopping_percent) || 0,
        groceries_percent: parseFloat(form.groceries_percent) || 0,
        fuel_percent: parseFloat(form.fuel_percent) || 0,
        entertainment_percent: parseFloat(form.entertainment_percent) || 0,
        priorities,
      };
      const res = await getAIRecommendation(payload);
      setRecommendations(res.data.recommendations);
      toast.success('AI recommendations ready!');
    } catch (err) {
      toast.error('Failed to get recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="ai-recommend-page">
      {/* Hero */}
      <section
        className="relative bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1746470427657-eb0b0115455f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBuZXR3b3JrJTIwYmx1ZXxlbnwwfHx8fDE3NzUyNjMwOTd8MA&ixlib=rb-4.1.0&q=85)' }}
      >
        <div className="bg-white/90 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="w-6 h-6 text-[#002FA7]" />
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#09090B]">AI Card Advisor</h1>
            </div>
            <p className="text-base text-[#52525B] max-w-xl">
              Tell us about your spending habits and priorities. Our AI will analyze {'>'}18 UAE credit cards to find your perfect match.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6" data-testid="ai-form">
            <div className="border border-[#E4E4E7] rounded-lg p-6">
              <h3 className="font-heading font-bold text-sm text-[#09090B] mb-4">Income & Spending</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#52525B]">Monthly Salary (AED)</Label>
                  <Input data-testid="input-salary" type="number" placeholder="e.g. 15000" value={form.salary} onChange={e => handleChange('salary', e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#52525B]">Monthly Card Spend (AED)</Label>
                  <Input data-testid="input-monthly-spend" type="number" placeholder="e.g. 5000" value={form.monthly_spend} onChange={e => handleChange('monthly_spend', e.target.value)} className="mt-1.5" />
                </div>
              </div>
            </div>

            <div className="border border-[#E4E4E7] rounded-lg p-6">
              <h3 className="font-heading font-bold text-sm text-[#09090B] mb-4">Spending Breakdown (%)</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'dining_percent', label: 'Dining' },
                  { key: 'travel_percent', label: 'Travel' },
                  { key: 'shopping_percent', label: 'Shopping' },
                  { key: 'groceries_percent', label: 'Groceries' },
                  { key: 'fuel_percent', label: 'Fuel' },
                  { key: 'entertainment_percent', label: 'Entertainment' },
                ].map(item => (
                  <div key={item.key}>
                    <Label className="text-[10px] uppercase tracking-wider text-[#52525B]">{item.label}</Label>
                    <Input
                      data-testid={`input-${item.key}`}
                      type="number"
                      value={form[item.key]}
                      onChange={e => handleChange(item.key, e.target.value)}
                      className="mt-1 h-9"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-[#E4E4E7] rounded-lg p-6">
              <h3 className="font-heading font-bold text-sm text-[#09090B] mb-4">Your Priorities</h3>
              <div className="space-y-3">
                {PRIORITIES.map(p => (
                  <label key={p.id} className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      data-testid={`priority-${p.id}`}
                      checked={priorities.includes(p.id)}
                      onCheckedChange={() => togglePriority(p.id)}
                    />
                    <span className="text-sm text-[#09090B]">{p.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              data-testid="get-recommendations-btn"
              disabled={loading}
              className="w-full h-11 bg-[#002FA7] hover:bg-[#002175] text-white font-medium gap-2"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Get AI Recommendations</>
              )}
            </Button>
          </form>

          {/* Results */}
          <div className="lg:col-span-3" data-testid="ai-results">
            {!recommendations && !loading && (
              <div className="border border-dashed border-[#E4E4E7] rounded-lg p-12 text-center">
                <Sparkles className="w-10 h-10 text-[#E4E4E7] mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-[#09090B] mb-2">Your Recommendations</h3>
                <p className="text-sm text-[#52525B]">Fill in your spending profile and click "Get AI Recommendations" to see personalized card matches.</p>
              </div>
            )}

            {loading && (
              <div className="border border-[#E4E4E7] rounded-lg p-12 text-center animate-pulse">
                <Loader2 className="w-10 h-10 text-[#002FA7] mx-auto mb-4 animate-spin" />
                <h3 className="font-heading font-bold text-lg text-[#09090B] mb-2">Analyzing Your Profile</h3>
                <p className="text-sm text-[#52525B]">Our AI is comparing your spending patterns against 18+ UAE credit cards...</p>
              </div>
            )}

            {recommendations && recommendations.length > 0 && (
              <div className="space-y-5">
                {recommendations.map((rec, i) => (
                  <div
                    key={rec.card_id || i}
                    className={`border border-[#E4E4E7] rounded-lg p-6 animate-fade-in-up stagger-${i + 1}`}
                    data-testid={`ai-recommendation-${i}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="bg-[#002FA7] text-white">#{i + 1} Match</Badge>
                          {rec.match_score && (
                            <span className="number-display text-xs font-medium text-[#002FA7]">{rec.match_score}% match</span>
                          )}
                        </div>
                        <h3 className="font-heading font-bold text-lg text-[#09090B]">{rec.card_name}</h3>
                        <span className="text-xs text-[#52525B]">{rec.bank_name}</span>
                      </div>
                      {rec.estimated_annual_savings > 0 && (
                        <div className="text-right">
                          <span className="text-[10px] uppercase tracking-wider text-[#52525B] font-medium">Est. Annual Savings</span>
                          <p className="number-display font-bold text-lg text-emerald-600">AED {rec.estimated_annual_savings?.toLocaleString()}</p>
                        </div>
                      )}
                    </div>

                    <Separator className="my-3" />

                    <div className="space-y-2 mb-4">
                      {rec.reasons?.map((reason, j) => (
                        <div key={j} className="flex items-start gap-2 text-sm text-[#52525B]">
                          <Target className="w-3.5 h-3.5 text-[#002FA7] mt-0.5 shrink-0" />
                          {reason}
                        </div>
                      ))}
                    </div>

                    {rec.tip && (
                      <div className="bg-[#F4F4F5] rounded-lg p-3 flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-[#52525B]">{rec.tip}</p>
                      </div>
                    )}

                    {rec.card_id && (
                      <Link to={`/cards/${rec.card_id}`} className="block mt-4">
                        <Button variant="outline" size="sm" className="text-xs gap-1.5">
                          View Card Details <ArrowRight className="w-3.5 h-3.5" />
                        </Button>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            )}

            {recommendations && recommendations.length === 0 && (
              <div className="border border-[#E4E4E7] rounded-lg p-12 text-center">
                <p className="text-[#52525B]">No recommendations could be generated. Please try adjusting your inputs.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
